import React, { useEffect, useId, useState } from "react";
import { Box, Tab, Tabs } from "@mui/material";
import { Stack } from "components/layout/reusableLayouts";
import PanelSection from "components/common/PanelSection";

export interface SectionTab {
  key: string;
  label: string;
  /** Status mark for the tab label, where the section carries one. */
  icon?: React.ReactElement;
  panel: React.ReactNode;
}

interface SectionTabsProps {
  tabs: SectionTab[];
  /** Names the strip for screen readers; there is no visible heading. */
  ariaLabel: string;
  /**
   * Bring a tab forward from outside, for a condition the reader has not acted
   * on yet. Setting state on the transition rather than overriding `value` is
   * what keeps a later click on another tab from being ignored.
   */
  focusKey?: string | null;
}

/**
 * A drawer's sections as one tab strip (D14).
 *
 * Keyed rather than indexed: a tab list grows as its feature and data queries
 * resolve, and an index chosen before that points at nothing.
 *
 * One tab is not a tab, so a single section renders under a `PanelSection`
 * heading instead. Call sites whose tabs are feature-gated therefore do not
 * need that branch themselves.
 */
const SectionTabs: React.FC<SectionTabsProps> = ({
  tabs,
  ariaLabel,
  focusKey,
}) => {
  const [activeKey, setActiveKey] = useState<string | null>(null);

  // MUI gives the strip its tablist/tab roles but not the panel, so the pairing
  // has to be named here or assistive tech cannot tell which content a tab
  // reveals. One instance id keeps the pairs unique when two strips coexist.
  const instanceId = useId();
  const tabId = (key: string) => `${instanceId}-tab-${key}`;
  const panelId = (key: string) => `${instanceId}-panel-${key}`;

  useEffect(() => {
    if (focusKey) setActiveKey(focusKey);
  }, [focusKey]);

  const activeTab = tabs.find((tab) => tab.key === activeKey) ?? tabs[0];

  if (!activeTab) return null;

  if (tabs.length === 1) {
    return (
      <PanelSection title={activeTab.label} icon={activeTab.icon}>
        {activeTab.panel}
      </PanelSection>
    );
  }

  return (
    <Stack gap="0">
      <Tabs
        value={activeTab.key}
        onChange={(_event, value: string) => setActiveKey(value)}
        aria-label={ariaLabel}
        scrollButtons="auto"
        allowScrollButtonsMobile
        // Scrollable, not fullWidth: a drawer is 420px, and the scroll
        // buttons above only render for this variant. Equal quarters truncate.
        variant="scrollable"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.key}
            value={tab.key}
            id={tabId(tab.key)}
            // Only the active panel is mounted, so pointing an inactive tab at
            // an id that does not exist is a broken relationship rather than a
            // described one. Mounting every panel instead would run each tab's
            // queries up front, which is the cost this strip exists to avoid.
            aria-controls={
              tab.key === activeTab.key ? panelId(tab.key) : undefined
            }
            label={tab.label}
            icon={tab.icon}
            iconPosition="start"
          />
        ))}
      </Tabs>

      <Box
        role="tabpanel"
        id={panelId(activeTab.key)}
        aria-labelledby={tabId(activeTab.key)}
        sx={{ paddingBlock: "var(--space-5)" }}
      >
        {activeTab.panel}
      </Box>
    </Stack>
  );
};

export default SectionTabs;
